import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,   OnDestroy, ViewContainerRef, ViewChild, ElementRef, TemplateRef,
  inject
} from '@angular/core';

import { IGlobalModal, D_GLOBAL_MODAL } from '../../../core/directives';
import { Subject } from 'rxjs';
import { debounceTime, distinct, map } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DocumentCodes } from '../../../core/enums';
import { OpenOrdersService } from '../core/services/OpenOrdersService';
import { GlobalModalsService, NotificationService } from '../../../core/services';
import { OpenOrdersServiceErrorResolverService } from '../core/services/error-resolvers/open-orders-service-error-resolver.service';
import { DeliveryNote, DeliveryNoteArticle } from '../core/interfaces';
import Fields from '../../../core/utils/fields';
import Files from '../../../core/utils/files';
import { ModalGeneric } from '../../../ui/generics';
import { QrcodeScannerComponent } from '../../../ui/qrcode-scanner/qrcode-scanner.component';
import { CartonsService } from '../../cartons/core/services/CartonsService';
import { ICartonType } from '../../cartons/core/interfaces/ICartonType';
import * as _ from 'lodash';

@Component({
    selector: 'app-delivery-note-process-modal',
    templateUrl: './delivery-note-process-modal.component.html',
    styleUrls: ['./delivery-note-process-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: D_GLOBAL_MODAL, useExisting: DeliveryNoteProcessModalComponent }],
    standalone: false
})
export class DeliveryNoteProcessModalComponent extends ModalGeneric implements OnInit, OnDestroy, IGlobalModal {
  @ViewChild('scanField') scanField: ElementRef<HTMLInputElement>;
  @ViewChild('eanField') eanField: ElementRef<HTMLInputElement>;
  private _orderNumber = '';
  private _orderNumberChange$ = new Subject();
  private _eanChange$ = new Subject();
  public deliveryNote: DeliveryNote;
  public loading = false;
  public ean: string;
  public isVisible = false;
  public title = 'Bestellung versenden';
  public pdfLink: string;
  public uncompletedReason = '';
  public isProcessedCorrect = false;
  public isManualInput = false;
  public cartonStep = false;
  public cartonTypes: ICartonType[] = [];
  public cartonQuantities: Record<number, number> = {};
  private readonly _globalModals = inject(GlobalModalsService);

  constructor(
    protected cdr: ChangeDetectorRef,
    private _modal: NzModalService,
    private _viewContainerRef: ViewContainerRef,
    private _service: OpenOrdersService,
    private _notifyService: NotificationService,
    private _errorResolver: OpenOrdersServiceErrorResolverService,
    private _cartonsService: CartonsService
  ) {
    super(cdr);
  }

  ngOnInit() {
    const numberChanged$ = this._orderNumberChange$
      .asObservable()
      .pipe(debounceTime(500));
    const eanChanged$ = this._eanChange$
      .asObservable()
      .pipe(debounceTime(500));

    numberChanged$.subscribe(this.loadDeliveryNote);
    eanChanged$.subscribe(this.eanScan);
  }

  set orderNumber(val: string) {
    if (val.slice(0, 2) === DocumentCodes.DELIVERY_NOTE) {
      this._orderNumber = val.slice(2);
      this._orderNumberChange$.next(this._orderNumber);
      return;
    }
    this._orderNumber = val;
  }

  get orderNumber() {
    return this._orderNumber;
  }

  afterModalOpen() {
    this.setFocusOnScanField();
  }

  notCompletedOrder(template: TemplateRef<any>) {

    this._modal.create({
      nzTitle: 'Grund angeben',
      nzContent: template,
      nzMaskClosable: false,
      nzClosable: false,
      nzOnOk: () => {
        this.setUnCompleted();
      }
    });
  }

  createBarcodeScannerModal(): void {
    const modal = this._modal.create({
      nzTitle: 'Barcode-Scanner',
      nzContent: QrcodeScannerComponent,
      nzViewContainerRef: this._viewContainerRef,
      nzWidth: 689,
      nzFooter: [
        {
          label: 'Ok',
          type: 'primary',
          onClick: () => modal.destroy()
        }
      ]
    });
    modal.afterClose.subscribe(result => {
      this.orderNumber = result?.code ?? '';
      this.cdr.detectChanges();
    });

    this.cdr.detectChanges();
  }

  setFocusOnScanField(): void {
    const focus = () => {
      const input = this.scanField?.nativeElement
        ?? document.querySelector<HTMLInputElement>(
          '.ship-process-modal:not(.ant-zoom-leave) .scan-field input'
        );
      input?.focus();
    };
    focus();
    setTimeout(focus, 100);
  }

  eanScan = (val: string): void  => {
    val = val.trim();
    if (val !== '') {
      if (this.cartonStep) {
        return;
      }
      if (!this.deliveryNote.orderCompleted) {
        this.analyzeProducts(val);
      } else {
        if (this.deliveryNote.checkTrackingNumber(val)) {
          this.enterCartonStep();
        } else {
          this._notifyService.pushError(undefined, this._errorResolver.getMessage('TrackCodeNotCorrect'));
          Fields.SetAsWrong(this.eanField, this.clearEan);
        }
      }
    }
  }

  handleForceFinish() {
    if (!this.deliveryNote.trackingCode && this.deliveryNote.orderCompleted) {
      this.enterCartonStep();
    } else {
      this._notifyService.pushError(undefined, this._errorResolver.getMessage('CantFinish'));
      Fields.PlayWrongScanSound();
    }
  }

  private enterCartonStep() {
    this.cartonStep = true;
    this.cartonQuantities = {};
    this.loading = true;
    this.cdr.detectChanges();
    this._cartonsService.getActive().subscribe(types => {
      this.cartonTypes = types;
      this.loading = false;
      this.cdr.detectChanges();
    }, (data) => {
      this.parseError(data);
    });
  }

  get cartonTotal(): number {
    return Object.values(this.cartonQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
  }

  incrementCarton(id: number): void {
    this.cartonQuantities[id] = (this.cartonQuantities[id] || 0) + 1;
    this.cdr.detectChanges();
  }

  decrementCarton(id: number): void {
    const next = (this.cartonQuantities[id] || 0) - 1;
    this.cartonQuantities[id] = next > 0 ? next : 0;
    this.cdr.detectChanges();
  }

  private resetCartonStep(): void {
    this.cartonStep = false;
    this.cartonTypes = [];
    this.cartonQuantities = {};
  }

  finishWithoutCartons(): void {
    this.submitFinish([]);
  }

  finishOrder() {
    const cartons = Object.entries(this.cartonQuantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([cartonTypeId, quantity]) => ({ cartonTypeId: Number(cartonTypeId), quantity }));
    if (!cartons.length) {
      this._notifyService.pushError(undefined, this._errorResolver.getMessage('CartonRequiredToFinish'));
      return;
    }
    this.submitFinish(cartons);
  }

  private submitFinish(cartons: { cartonTypeId: number; quantity: number }[]): void {
    this.loading = true;
    this.cdr.detectChanges();
    this._service.finishOpenOrder(this.deliveryNote.id.toString(), cartons).subscribe((result) => {
      this.deliveryNote = null;
      this.loading = false;
      this.resetCartonStep();
      this.orderNumber = '';
      this.scanField.nativeElement.focus();
      this.clearEan();
      this.showCorrectBanner();
      if (result?.belowMinStock?.length) {
        const names = result.belowMinStock.map(c => `${c.name} (${c.stock})`).join(', ');
        this._notifyService.pushWarning('Meldebestand', `Bestand unter Meldebestand: ${names}`);
      }
    }, (data) => {
      Fields.PlayWrongScanSound();

      if (data && data.InternalCode === 'ErrorOnFinishOpenOrder') {
        this.loading = false;
        this._notifyService.pushError('Fehlgeschlagen', `Bestellung bereits abgeschlossen: ${ data.Message }`);
      } else {
        this.parseError(data);
      }
    });
  }

  private setUnCompleted() {
    this.loading = true;
    this.cdr.detectChanges();
    this._service.setUncompletedOrder(this.deliveryNote.id.toString(), this.uncompletedReason).subscribe(() => {
      this.deliveryNote = null;
      this.loading = false;
      this.orderNumber = '';
      this.scanField.nativeElement.focus();
      this.clearEan();
      this.showCorrectBanner();
    }, (data) => {
      Fields.PlayWrongScanSound();
      this.parseError(data);
    });
  }

  private showCorrectBanner() {
    this.isProcessedCorrect = true;
    setTimeout(() => {
      this.isProcessedCorrect = false;
      this.cdr.detectChanges();
    }, 2000);
    this.cdr.detectChanges();
  }

  private analyzeProducts(value: string): boolean {
    const product = this.searchProduct(value);

    if (product) {
      if (!product.incrementNumberOfScan() ) {
        this._notifyService.pushWarning(undefined , this._errorResolver.getMessage('ProductTooMuch'));

        Fields.SetAsWrong(this.eanField, this.clearEan);
      } else {
        this._notifyService.pushSuccess(undefined , 'Produkt wurde hinzugefügt');
        this.ean = '';
      }
      this.checkCompletedProducts(product);
      this.cdr.detectChanges();
      return true;
    }

    Fields.SetAsWrong(this.eanField, this.clearEan);
    return false;
  }

  private checkCompletedProducts(product) {
    if (product.isFull()) {
      this.deliveryNote.checkIsOrderCompleted();
      if (this.deliveryNote.orderCompleted) {
        if (this.deliveryNote.deliveryMethod === 'CC') {

          this._modal.confirm({
            nzTitle: 'Info',
            nzContent: 'Bestellung vollständig: Click & Collect',
            nzOnOk: () => this.enterCartonStep()
          });
          return;
        }

        if (this.deliveryNote.trackingCode) {
          this.printTrackingSlip();
        } else {
          Fields.PlayWrongScanSound();
          this._notifyService.pushWarning(undefined , this._errorResolver.getMessage('TrackingCodeNotExists'));
        }
      }
    }
  }

  private printTrackingSlip() {
    Files.openPdfByBase64(this.deliveryNote.deliverySlip, link => {
      this.pdfLink = link;
      this.cdr.detectChanges();
    });
  }




  private loadDeliveryNote = () => {
    this.loading = true;
    this.cdr.detectChanges();
    this._service.getDeliveryNoteByOrderNumber(this._orderNumber).pipe(
      map(value => {
        return new DeliveryNote({
          ...value,
          articles: _(value.articles.filter(art => (art.orderNumber.toLowerCase() !== 'Spende'.toLocaleLowerCase())))
            .groupBy('orderNumber')
            .map((article, id) => (new DeliveryNoteArticle({
              ...article[0],
              quantity: _.sumBy(article, 'quantity'),
            } as any))).value()
        });
      })
    ).subscribe((val: DeliveryNote) => {
      this.deliveryNote = val;
      this.uncompletedReason = val.internalComment;
      this.resetCartonStep();
      this.loading = false;
      this.eanField.nativeElement.focus();
      this.cdr.detectChanges();
    }, (data) => {
      Fields.PlayWrongScanSound();
      if (!this.isManualInput) {
        this._orderNumber = '';
        this.cdr.detectChanges();
      }
      this.parseError(data);
    });
  }

  private searchProduct(val: string): DeliveryNoteArticle|null {
    return this.deliveryNote.articles.find(a => a.ean === val && !a.isFull())
      ?? this.deliveryNote.articles.find(a => a.ean === val);
  }

  private searchProductById(id: number): DeliveryNoteArticle|null {
    return this.deliveryNote.articles.find(variant => variant.id === id);
  }

  handleCancel() {
    super.handleCancel();
    this.ean = '';
    this.orderNumber = '';
    this.deliveryNote = null;
    this.resetCartonStep();
    this._globalModals.leaveMenuModalRoute();
  }

  handleNumberScan = (val): void => {
    if (val === '') {
      this.deliveryNote = null;
      return;
    }
    this._orderNumberChange$.next(val);
  }

  handleEANScan = (val): void => this._eanChange$.next(val);

  handleBarcodeScanner = (): void => this.createBarcodeScannerModal();

  markAsFull(id: number) {
    const product = this.searchProductById(id);
    product.makeFull();
    this.checkCompletedProducts(product);
    this.cdr.detectChanges();
  }

  clearEan = (): void => {
    this.ean = '';
    this.cdr.detectChanges();
  }

  private parseError = data => {
    this.loading = false;
    this._notifyService.pushError('Fehlgeschlagen', this._errorResolver.getMessage((data && data.InternalCode) ? data.InternalCode : ''));
  }




  ngOnDestroy(): void {

  }
}
